import React, { useState, useEffect } from 'react';
import { Form, Button, Alert, Card } from 'react-bootstrap';
import { FaStar } from 'react-icons/fa';

export default function ReviewForm({ spotifyId, type, itemName, itemImage, onReviewSubmitted }) {
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [review, setReview] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');



  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (rating === 0) {
      setError('Please select a rating');
      return;
    }
    
    if (!review.trim()) {
      setError('Please write a review');
      return;
    }

    if (review.length > 1000) {
      setError('Review cannot exceed 1000 characters');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      const reviewData = {
        spotifyId,
        type,
        rating,
        review: review.trim(),
        itemName,
        itemImage
      };
      
      const response = await fetch('/api/reviews', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(reviewData)
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess('Review submitted successfully!');
        // Clear form after successful submission
        setRating(0);
        setReview('');
        if (onReviewSubmitted) {
          onReviewSubmitted(data); // Pass the review data back
        }
        
        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.message || 'Failed to submit review');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getStarValue = (e, i) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const halfPoint = rect.width / 2;
    // Only .5 or full star
    return mouseX < halfPoint ? i - 0.5 : i;
  };

  const renderStars = () => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <div
          key={i}
          style={{
            position: 'relative',
            display: 'inline-block',
            cursor: 'pointer',
            marginRight: '2px'
          }}
          onClick={e => setRating(getStarValue(e, i))}
          onMouseMove={e => setHover(getStarValue(e, i))}
          onMouseLeave={() => setHover(0)}
        >
          {/* Background star (always gray) */}
          <FaStar
            color="#e4e5e9"
            style={{ fontSize: '1.5rem' }}
          />
          {/* Foreground star (colored based on rating) */}
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: `${((hover || rating) >= i) ? 100 : ((hover || rating) >= i - 0.5 ? 50 : 0)}%`,
              overflow: 'hidden',
              transition: 'width 0.1s ease'
            }}
          >
            <FaStar
              color="#ffc107"
              style={{ fontSize: '1.5rem' }}
            />
          </div>
        </div>
      );
    }
    return stars;
  };

  const getRatingText = () => {
    if (rating === 0) return 'Select rating';
    if (rating === 0.5) return 'Very Poor';
    if (rating === 1) return 'Poor';
    if (rating === 1.5) return 'Below Average';
    if (rating === 2) return 'Fair';
    if (rating === 2.5) return 'Average';
    if (rating === 3) return 'Good';
    if (rating === 3.5) return 'Above Average';
    if (rating === 4) return 'Very Good';
    if (rating === 4.5) return 'Excellent';
    if (rating === 5) return 'Outstanding';
    return `${rating} stars`;
  };

  return (
    <Card className="mb-4">
      <Card.Header>
        <h5 className="mb-0">
          Write a Review
        </h5>
      </Card.Header>
      <Card.Body>
        {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
        {success && <Alert variant="success" onClose={() => setSuccess('')} dismissible>{success}</Alert>}
        
        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label>Rating</Form.Label>
            <div className="d-flex align-items-center">
              <div className="me-3">
                {renderStars()}
              </div>
              <span className="text-muted">{getRatingText()}</span>
            </div>
            <Form.Text className="text-muted">
              Click on the stars to rate from 1 to 5
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Review</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              placeholder="Share your thoughts about this music..."
              value={review}
              onChange={(e) => setReview(e.target.value)}
              maxLength={1000}
              isInvalid={review.length > 1000}
            />
            <Form.Text className="text-muted">
              {review.length}/1000 characters
            </Form.Text>
            {review.length > 1000 && (
              <Form.Control.Feedback type="invalid">
                Review cannot exceed 1000 characters
              </Form.Control.Feedback>
            )}
          </Form.Group>

          <Button 
            type="submit" 
            variant="primary" 
            disabled={loading}
            className="w-100"
          >
            {loading ? 'Submitting...' : 'Submit Review'}
          </Button>
        </Form>
      </Card.Body>
    </Card>
  );
} 